/**
 * Phase 2 — Offline retry queue for chat sends.
 *
 * Lightweight IndexedDB-backed queue. When a chat send fails because the
 * device is offline (or the network blip is transient), the message is
 * stored locally with its idempotency_key. When the browser reports
 * connectivity again, we replay the queue in order.
 *
 * The send-handler is registered once (typically at app boot) and the queue
 * tries to flush on:
 *   - explicit enqueue (in case we just came back online)
 *   - window "online" event
 *   - tab becoming visible
 */

export interface QueuedMessage {
  id: string;             // local uuid
  consultation_id: string;
  sender_id: string;
  sender_type: "patient" | "doctor";
  content: string;
  message_type: "text" | "image" | "file";
  idempotency_key: string;
  message_hash?: string | null;
  created_at: string;
  attempts: number;
}

const DB_NAME = "ilajly-offline";
const STORE = "chat-queue";
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("created_at", "created_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => Promise<T> | T): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const result = Promise.resolve(fn(store));
    t.oncomplete = () => result.then(resolve, reject);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function enqueueMessage(msg: Omit<QueuedMessage, "attempts">): Promise<void> {
  await tx("readwrite", (store) => {
    store.put({ ...msg, attempts: 0 } satisfies QueuedMessage);
  });
  notifyChange();
  // Try to flush immediately — handler decides whether network is available
  void flushQueue();
}

export async function getQueuedMessages(): Promise<QueuedMessage[]> {
  return tx("readonly", (store) => {
    return new Promise<QueuedMessage[]>((resolve, reject) => {
      const req = store.index("created_at").getAll();
      req.onsuccess = () => resolve((req.result as QueuedMessage[]) || []);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function removeQueued(id: string): Promise<void> {
  await tx("readwrite", (store) => store.delete(id));
  notifyChange();
}

async function bumpAttempts(id: string): Promise<void> {
  await tx("readwrite", (store) => {
    return new Promise<void>((resolve) => {
      const req = store.get(id);
      req.onsuccess = () => {
        const row = req.result as QueuedMessage | undefined;
        if (row) {
          row.attempts = (row.attempts || 0) + 1;
          store.put(row);
        }
        resolve();
      };
      req.onerror = () => resolve();
    });
  });
}

type SendHandler = (msg: QueuedMessage) => Promise<boolean>;
let sendHandler: SendHandler | null = null;
let flushing = false;

const MAX_ATTEMPTS = 8;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — drop stale queued messages

type DropReason = "expired" | "max_attempts";
type DropListener = (msg: QueuedMessage, reason: DropReason) => void;
const dropListeners = new Set<DropListener>();

type ChangeListener = () => void;
const changeListeners = new Set<ChangeListener>();

export function registerSendHandler(handler: SendHandler): void {
  sendHandler = handler;
}

export function onQueueDrop(listener: DropListener): () => void {
  dropListeners.add(listener);
  return () => dropListeners.delete(listener);
}

export function onQueueChange(listener: ChangeListener): () => void {
  changeListeners.add(listener);
  return () => { changeListeners.delete(listener); };
}

function notifyChange() {
  for (const l of changeListeners) {
    try { l(); } catch { /* noop */ }
  }
}

function notifyDrop(msg: QueuedMessage, reason: DropReason) {
  for (const l of dropListeners) {
    try { l(msg, reason); } catch { /* noop */ }
  }
}

export async function flushQueue(): Promise<void> {
  if (flushing) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  if (!sendHandler) return;

  flushing = true;
  try {
    const items = await getQueuedMessages();
    for (const item of items) {
      // Drop expired (>24h) — protects against replaying into closed/expired consultations
      const ageMs = Date.now() - new Date(item.created_at).getTime();
      if (ageMs > MAX_AGE_MS) {
        await removeQueued(item.id);
        notifyDrop(item, "expired");
        continue;
      }
      // Cap retries to avoid spinning forever on a permanent failure
      if (item.attempts >= MAX_ATTEMPTS) {
        await removeQueued(item.id);
        notifyDrop(item, "max_attempts");
        continue;
      }
      try {
        const ok = await sendHandler(item);
        if (ok) {
          await removeQueued(item.id);
        } else {
          await bumpAttempts(item.id);
          // Stop on failure — preserves order and avoids hammering
          break;
        }
      } catch {
        await bumpAttempts(item.id);
        break;
      }
    }
  } finally {
    flushing = false;
  }
}

let listenersInstalled = false;
export function installOfflineQueueListeners(): void {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;
  window.addEventListener("online", () => void flushQueue());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flushQueue();
  });
}
